/**
 * Web Health disclosure: the twin of `HealthDisclosure.tsx`. A quiet muted
 * header with a trailing chevron ("What we checked", "View individual
 * suggestions", "Checks in this area") over a panel that grows to its natural
 * height (`.tabbio-health-disclosure`). Closed panels are inert. With
 * `rememberKey` the open state survives the overview unmounting.
 */
import { useId, useState } from 'react';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { getRememberedOpen, rememberOpen } from './health-disclosure-memory';
import type { HealthDisclosureProps } from './health-props';

export type { HealthDisclosureProps };

export function HealthDisclosure({
  label,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  rememberKey,
  children,
  testID,
}: HealthDisclosureProps) {
  const [openState, setOpenState] = useState(() => getRememberedOpen(rememberKey, defaultOpen));
  const open = openProp ?? openState;
  const toggle = () => {
    if (openProp === undefined) {
      setOpenState(!open);
      rememberOpen(rememberKey, !open);
    }
    onOpenChange?.(!open);
  };
  const panelId = `tabbio-health-disc-${useId().replace(/:/g, '')}`;
  return (
    <div className="tabbio-health-disc" data-open={open} data-testid={testID}>
      <button
        type="button"
        className="tabbio-health-disc-header"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
      >
        <span>{label}</span>
        <span className={`tabbio-health-chevron${open ? ' is-open' : ''}`}>
          <MaterialSymbol name="expand_more" size={20} />
        </span>
      </button>
      <div
        id={panelId}
        className={`tabbio-health-disclosure${open ? ' is-open' : ''}`}
        aria-hidden={!open}
        inert={!open}
      >
        <div>
          <div className="tabbio-health-disc-body">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default HealthDisclosure;
